import { motion } from 'framer-motion';
import { displayFont } from './shared';
import type { LpEmotiveStatement } from '../../data/landingPages';
import { CDN_BASE } from '../../config/cdn';

const IMAGES = [
  `${CDN_BASE}/images/placeholders/wedding-1.jpg`,
  `${CDN_BASE}/images/placeholders/wedding-2.jpg`,
  `${CDN_BASE}/images/placeholders/wedding-3.jpg`,
];

const DEFAULT_STATEMENTS: LpEmotiveStatement[] = [
  {
    heading: 'Your love story deserves more than a group chat',
    highlight: 'group chat',
    copy: "Every couple's journey is unique. Your invitation should feel just as personal — a beautiful first glimpse of the day you've been dreaming about.",
  },
  {
    heading: 'First impressions that set the tone',
    highlight: 'set the tone',
    copy: "Your invite is the very first thing your guests see. Make it something they'll talk about — something that builds excitement from the moment they open it.",
  },
  {
    heading: 'Less admin, more anticipation',
    highlight: 'anticipation',
    copy: "Spend your time choosing flowers, not chasing RSVPs. Let your invite handle the details while you focus on what actually matters.",
  },
];

function renderHeading(heading: string, highlight: string) {
  const idx = heading.indexOf(highlight);
  if (idx === -1) return heading;
  return (
    <>
      {heading.slice(0, idx)}
      <span className="text-primary">{highlight}</span>
      {heading.slice(idx + highlight.length)}
    </>
  );
}

export function EmotiveSection({ statements = DEFAULT_STATEMENTS }: { statements?: LpEmotiveStatement[] }) {

  return (
    <section className="py-20 md:py-32 bg-background-card overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-20 md:space-y-32">
          {statements.map((statement, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className={`flex flex-col ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} items-center gap-10 md:gap-16`}
            >
              {/* Image */}
              <div className="w-full md:w-1/2">
                <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
                  <img
                    src={IMAGES[i % IMAGES.length]}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
              {/* Text */}
              <div className="w-full md:w-1/2 text-center md:text-left">
                <h2
                  className="text-3xl md:text-4xl lg:text-[2.75rem] leading-tight text-text-primary mb-5"
                  style={displayFont}
                >
                  {renderHeading(statement.heading, statement.highlight)}
                </h2>
                <p className="text-base md:text-lg lg:text-xl text-text-secondary leading-relaxed max-w-lg mx-auto md:mx-0">
                  {statement.copy}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
